import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config/api";

function About() {
  const [activeSkill, setActiveSkill] = useState(null);
  const [about, setAbout] = useState({
    description: "",
    secondDescription: "",
    skills: {},
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAbout = async (retries = 3, delay = 1000) => {
      try {
        const response = await axios.get(`${API_URL}/about`);
        if (response.data && response.data.success) {
          setAbout(response.data.data);
        } else if (response.data && response.data.length > 0) {
          setAbout(response.data[0]);
        }
        setLoading(false);
      } catch (error) {
        if (error.response?.status === 429 && retries > 0) {
          console.log(
            `Rate limited. Retrying in ${delay}ms... (${retries} attempts left)`
          );
          setTimeout(() => fetchAbout(retries - 1, delay * 2), delay);
          return;
        }
        console.error("Error fetching about:", error);
        setLoading(false);
      }
    };

    fetchAbout();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <div className="text-white/30 text-sm tracking-wider uppercase">
          Loading...
        </div>
      </div>
    );
  }

  const skills = about.skills || {};

  return (
    <div className="min-h-screen text-white py-16 sm:py-24 md:py-32 px-4 sm:px-6 relative overflow-hidden">
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-[0.015]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        ></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* ---------- ABOUT ME SECTION ---------- */}
        <div className="mb-20 sm:mb-28 md:mb-32">
          {/* Section indicator */}
          <div className="flex items-center gap-3 sm:gap-4 mb-10 sm:mb-14 md:mb-16">
            <div className="w-1 h-1 rounded-full bg-white/30"></div>
            <span className="text-white/30 text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase font-light">
              About Me
            </span>
            <div className="h-[1px] flex-1 bg-gradient-to-r from-white/10 to-transparent"></div>
          </div>

          {/* Content Grid */}
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-12 md:gap-16 items-start">
            {/* Left: Visual Element */}
            <div className="relative hidden lg:block">
              <div
                className="aspect-square rounded-3xl bg-white/[0.01] border border-white/[0.04] 
                            backdrop-blur-sm overflow-hidden relative group"
              >
                {/* Geometric pattern */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-full h-full p-16">
                    {/* Concentric squares */}
                    <div
                      className="absolute inset-0 m-auto w-64 h-64 border border-white/[0.06] rounded-2xl 
                                  group-hover:rotate-45 transition-transform duration-[3000ms] ease-in-out"
                    ></div>
                    <div
                      className="absolute inset-0 m-auto w-48 h-48 border border-white/[0.04] rounded-2xl 
                                  group-hover:rotate-[60deg] transition-transform duration-[4000ms] ease-in-out"
                    ></div>
                    <div
                      className="absolute inset-0 m-auto w-32 h-32 border border-white/[0.03] rounded-2xl 
                                  group-hover:rotate-90 transition-transform duration-[5000ms] ease-in-out"
                    ></div>

                    {/* Center icon */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div
                        className="w-16 h-16 bg-white/[0.02] rounded-xl border border-white/[0.06] 
                                    flex items-center justify-center backdrop-blur-xl"
                      >
                        <svg
                          className="w-7 h-7 text-white/30"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Corner accents */}
                <div className="absolute top-4 left-4 w-8 h-8 border-l border-t border-white/10 rounded-tl-lg"></div>
                <div className="absolute bottom-4 right-4 w-8 h-8 border-r border-b border-white/10 rounded-br-lg"></div>
              </div>
            </div>

            {/* Right: Text Content */}
            <div className="space-y-6 sm:space-y-8">
              <div className="space-y-4 sm:space-y-6">
                <p className="text-white/50 text-base sm:text-base leading-relaxed font-light">
                  {about.description}
                </p>

                {about.secondDescription && (
                  <p className="text-white/40 text-base sm:text-base leading-relaxed font-light">
                    {about.secondDescription}
                  </p>
                )}
              </div>

              {/* Status badge */}
              <div
                className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 bg-white/[0.02] 
                            border border-white/[0.06] rounded-full"
              >
                <div className="w-2 h-2 rounded-full bg-white/30 animate-pulse"></div>
                <span className="text-white/30 text-xs tracking-wider uppercase font-light">
                  Open to Collaborate
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- SKILLS SECTION ---------- */}
        <div>
          {/* Section indicator */}
          <div className="flex items-center gap-3 sm:gap-4 mb-10 sm:mb-14 md:mb-16">
            <div className="w-1 h-1 rounded-full bg-white/30"></div>
            <span className="text-white/30 text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase font-light">
              Technical Skills
            </span>
            <div className="h-[1px] flex-1 bg-gradient-to-r from-white/10 to-transparent"></div>
          </div>

          {/* Skills Grid */}
          <div className="space-y-8 sm:space-y-10 md:space-y-12">
            {Object.entries(skills).map(([category, items], idx) => (
              <div
                key={category}
                className="group"
                style={{
                  animation: `fadeInUp 0.8s ease-out ${idx * 0.1}s both`,
                }}
              >
                {/* Category Header */}
                <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5 md:mb-6">
                  <h3 className="text-white/60 text-xs sm:text-sm tracking-wider uppercase font-light">
                    {category}
                  </h3>
                  <div className="h-[1px] flex-1 bg-white/[0.04]"></div>
                  <span className="text-white/20 text-xs font-light">
                    {items.length}
                  </span>
                </div>

                {/* Skills Pills */}
                <div className="flex flex-wrap gap-2 sm:gap-2.5 md:gap-3">
                  {items.map((skill) => (
                    <button
                      key={skill}
                      onMouseEnter={() => setActiveSkill(skill)}
                      onMouseLeave={() => setActiveSkill(null)}
                      className="relative px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 bg-white/[0.02] border border-white/[0.06] 
                               rounded-full hover:bg-white/[0.04] hover:border-white/[0.12] 
                               transition-all duration-500 group/skill"
                    >
                      <span
                        className={`text-xs sm:text-sm font-light transition-colors duration-500 ${
                          activeSkill === skill
                            ? "text-white/70"
                            : "text-white/30"
                        }`}
                      >
                        {skill}
                      </span>

                      {/* Hover indicator */}
                      <div
                        className="absolute inset-0 rounded-full bg-white/[0.02] opacity-0 
                                    group-hover/skill:opacity-100 transition-opacity duration-500"
                      ></div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating accent */}
        <div
          className="absolute top-1/4 -right-32 w-96 h-96 bg-white/[0.008] 
                      blur-[150px] rounded-full"
        ></div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default About;
